import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import ReactSelect, { MultiValue } from 'react-select';
import { Coupon } from '../../../types/coupons';
import SimpleModal from '../../../components/SimpleModal/SimpleModal';

/* ---------- helper types for react-select ---------- */
type Option = { value: string | number; label: string };

interface Props {
  TriggerComponent: React.ReactElement<any>;
  onSubmit: (data: Omit<Coupon, 'id' | 'usageCount' | 'active'>) => void;
  getTypesId: (orderType: string) => Promise<any[]>;
}

export default function CreateCouponModal({
  TriggerComponent,
  onSubmit,
  getTypesId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [typesList, setTypesList] = useState<any[]>([]);

  const initialFormState: Omit<Coupon, 'id' | 'usageCount' | 'active'> = {
    code: '',
    description: '',
    orderType: null,
    discountType: '',
    discountValue: 1,
    typeId: null,
    maxDiscount: null,
    minimumOrderValue: null,
    rule: null,
    startDate: null,
    expirationDate: null,
    startHour: null,
    endHour: null,
    daysOfWeek: null,
    usageLimit: null,
  };

  const [form, setForm] = useState<Omit<Coupon, 'id' | 'usageCount' | 'active'>>(
    initialFormState
  );

  function update(key: keyof typeof form, value: any) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    async function loadTypes() {
      if (!form.orderType) {
        setTypesList([]);
        setForm((prev) => ({ ...prev, typeId: null }));
        return;
      }
      try {
        const orderTypeValue = Array.isArray(form.orderType)
          ? form.orderType[0]
          : form.orderType;
        const resp = await getTypesId(orderTypeValue);
        setTypesList(resp || []);
      } catch (err) {
        console.error('Erro ao carregar types:', err);
        setTypesList([]);
      }
    }
    loadTypes();
  }, [form.orderType, getTypesId]);

  const handleClear = () => {
    setForm(initialFormState);
  };

  const handleClose = () => {
    handleClear();
  };

  const submitForm = () => {
    const payload: Omit<Coupon, 'id' | 'usageCount' | 'active'> = {
      ...form,

      description: form.description || null,
      orderType:
        (form.orderType && (Array.isArray(form.orderType) ? form.orderType : [form.orderType])) ||
        null,
      typeId: form.typeId || null,
      rule: form.rule && form.rule.length > 0 ? form.rule : null,
      daysOfWeek:
        form.daysOfWeek && form.daysOfWeek.length > 0 ? form.daysOfWeek : null,
      maxDiscount: form.maxDiscount || null,
      minimumOrderValue: form.minimumOrderValue || null,
      usageLimit: form.usageLimit || null,

      startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
      expirationDate: form.expirationDate
        ? new Date(form.expirationDate).toISOString()
        : null,
    };

    onSubmit(payload);
    handleClear();
    setOpen(false);
  };

  /* react-select options para typeId (single) */
  const typeOptions: Option[] = typesList.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  /* react-select options para daysOfWeek (multi) */
  const weekOptions: Option[] = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda' },
    { value: 2, label: 'Terça' },
    { value: 3, label: 'Quarta' },
    { value: 4, label: 'Quinta' },
    { value: 5, label: 'Sexta' },
    { value: 6, label: 'Sábado' },
  ];

  /* valor atual do daysOfWeek para o multi-select */
  const currentDaysValue: Option[] = (form.daysOfWeek ?? []).map((n) => {
    const found = weekOptions.find((o) => o.value === n);
    return found ?? { value: n, label: String(n) };
  });

  const rsStyles = {
    control: (base: any) => ({
      ...base,
      borderRadius: 4, // match MUI Textfield if possible
      borderColor: 'rgba(0, 0, 0, 0.23)',
      minHeight: '56px', // match MUI TextField approx
      '&:hover': {
         borderColor: 'rgba(0, 0, 0, 0.87)',
      }
    }),
    menu: (base: any) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 1400 }),
  };

  const modalBody = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Código"
          variant="outlined"
          value={form.code}
          onChange={(e) => update('code', e.target.value)}
        />
        <TextField
          fullWidth
          label="Tipo de desconto"
          variant="outlined"
          select
          value={form.discountType}
          onChange={(e) => update('discountType', e.target.value)}
        >
          <MenuItem value="">Selecione...</MenuItem>
          <MenuItem value="PERCENTAGE">Porcentagem</MenuItem>
          <MenuItem value="AMOUNT">Valor fixo</MenuItem>
        </TextField>
      </Stack>

      <TextField
        fullWidth
        label="Descrição"
        variant="outlined"
        value={form.description ?? ''}
        onChange={(e) => update('description', e.target.value)}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Valor do desconto"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 1 } }}
          value={form.discountValue}
          onChange={(e) => update('discountValue', Number(e.target.value || 0))}
        />
        <TextField
          fullWidth
          label="Desconto máximo"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 1 } }}
          value={form.maxDiscount ?? ''}
          onChange={(e) => update('maxDiscount', e.target.value ? Number(e.target.value) : null)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
         <TextField
          fullWidth
          label="Serviço"
          variant="outlined"
          select
          value={form.orderType ?? ''}
          onChange={(e) => update('orderType', e.target.value || null)}
         >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="WASHING">Lavação</MenuItem>
          <MenuItem value="LAUNDRY">Lavanderia</MenuItem>
          <MenuItem value="GAS">Combustível</MenuItem>
          <MenuItem value="VACUUM">Aspirador</MenuItem>
         </TextField>

        {form.orderType &&
          (Array.isArray(form.orderType) ? form.orderType[0] : form.orderType) !== 'VACUUM' &&
          (Array.isArray(form.orderType) ? form.orderType[0] : form.orderType) !== 'GAS' ? (
              <TextField
                fullWidth
                label="Tipo do serviço"
                variant="outlined"
                select
                value={form.typeId ?? ''}
                onChange={(e) => update('typeId', e.target.value)}
              >
                <MenuItem value="">Selecione...</MenuItem>
                {typesList.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {t.type === 'VAN' ? ' - Van' : t.type === 'CAR' ? ' - Carro' : ''}
                  </MenuItem>
                ))}
              </TextField>
          ) : (
             <Box sx={{ width: '100%', display: { xs: 'none', sm: 'block' } }} />
          )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Valor mínimo"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 1 } }}
          value={form.minimumOrderValue ?? ''}
          onChange={(e) => update('minimumOrderValue', e.target.value ? Number(e.target.value) : null)}
        />
        <TextField
          fullWidth
          label="Regra"
          variant="outlined"
          select
          value={form.rule?.[0] ?? ''}
          onChange={(e) => update('rule', e.target.value ? [e.target.value] : null)}
        >
          <MenuItem value="">Nenhum</MenuItem>
          <MenuItem value="FIRST_PURCHASE">Primeira compra</MenuItem>
        </TextField>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Data de início"
          variant="outlined"
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          value={form.startDate ?? ''}
          onChange={(e) => update('startDate', e.target.value || null)}
        />
        <TextField
          fullWidth
          label="Data de expiração"
          variant="outlined"
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          value={form.expirationDate ?? ''}
          onChange={(e) => update('expirationDate', e.target.value || null)}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          label="Hora inicial"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 0, max: 23 } }}
          value={form.startHour ?? ''}
          onChange={(e) => update('startHour', e.target.value === '' ? null : Number(e.target.value))}
        />
        <TextField
          fullWidth
          label="Hora final"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 0, max: 23 } }}
          value={form.endHour ?? ''}
          onChange={(e) => update('endHour', e.target.value === '' ? null : Number(e.target.value))}
        />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ position: "relative", width: "100%" }}>
          <ReactSelect
            isMulti
            options={weekOptions}
            value={currentDaysValue}
            placeholder="Dias da semana permitidos..."
            onChange={(selected: MultiValue<Option>) => {
              const values = (selected || []).map((s) => Number(s.value));
              update('daysOfWeek', values.length > 0 ? values : null);
            }}
            isClearable
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={rsStyles}
          />
        </Box>
        <TextField
          fullWidth
          label="Limite de usos"
          variant="outlined"
          type="number"
          slotProps={{ htmlInput: { min: 1 } }}
          value={form.usageLimit ?? ''}
          onChange={(e) => update('usageLimit', e.target.value ? Number(e.target.value) : null)}
        />
      </Stack>
    </Box>
  );

  return (
    <SimpleModal
      onConfirm={submitForm}
      onClose={handleClose}
      open={open}
      setOpen={setOpen}
      Component={TriggerComponent}
      title="Criar Cupom"
      confirmText="Criar"
      cancelText="Cancelar"
      body={modalBody}
      maxWidth="md"
    />
  );
}
